import { config } from "config";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

export type SimTask = {
  id: string;
  payload: string;
  status: string;
  task_type: string;
  created_at: number;
  execution_at: number;
  finished_at: number | null;
  user_id: string;
  result: string | null;
  execute_logs: string | null;
};

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (reason: any) => void;
};

export const useSimSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const pendingRef = useRef<Map<string, PendingRequest>>(new Map());
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const token = window.sessionStorage.getItem("authorization");
    const user = (() => {
      try { return JSON.parse(window.sessionStorage.getItem("user") as string); } catch { return null; }
    })();
    const channelId = `api-resp___project___orca___user_id___${user?.id}`;

    const socket = io(config().server.sim, {
      path: "/socket.io/sio-svc/",
      transports: ["websocket"],
      query: { jwt: token },
    });
    socketRef.current = socket;

    // api-resp channel is auto-joined by the server on connect via JWT
    socket.on("joinResult", (resp: any) => {
      if (!resp?.accepted) return;
      // Authenticate the socket session
      const caller_req_id = `admin-login-${nanoid()}`;
      pendingRef.current.set(caller_req_id, {
        resolve: () => {
          socket.emit("join", {
            provider: "personal-event",
            selector: { user_id: String(user?.id), project: "orca" },
          });
          setConnected(true);
        },
        reject: (e) => console.error("[useSimSocket] login failed:", e),
      });
      socket.emit("apiReq", {
        type: "apiReq",
        steps: [{ name: "client req", started_at: Date.now() }],
        payload: {
          caller_req_id,
          req: { path: "/login", method: "POST" },
        },
      });
    });
    socket.on("disconnect", () => setConnected(false));

    socket.on("apiResp", (resp: any) => {
      console.log("[useSimSocket] apiResp raw:", JSON.stringify(resp));
      const inner = resp?.body?.payload;
      const reqId = inner?.caller_req_id;
      const pending = pendingRef.current.get(reqId);
      if (!pending) return;
      pendingRef.current.delete(reqId);
      if (inner?.http_code === 200) {
        pending.resolve(inner.http_result);
      } else {
        pending.reject(new Error(inner?.http_err || "Request failed"));
      }
    });

    socket.on("personalEvent", (resp: any) => {
      const events: any[] = resp?.body?.payload?.events ?? [];
      events.forEach((evt) => {
        const data = evt?.data;
        // personalEvent has no caller_req_id; resolve the oldest pending POST request
        const firstKey = pendingRef.current.keys().next().value;
        if (!firstKey) return;
        const pending = pendingRef.current.get(firstKey);
        if (!pending) return;
        pendingRef.current.delete(firstKey);
        if (data?.status === "SUCCEEDED") {
          pending.resolve(data);
        } else {
          pending.reject(new Error(data?.status || "Task failed"));
        }
      });
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Stable reference — only depends on refs, never needs to change
  const emit = useCallback((path: string, method: string, req: Record<string, any> = {}): Promise<any> => {
    return new Promise((resolve, reject) => {
      const socket = socketRef.current;
      if (!socket?.connected) {
        reject(new Error("Socket not connected"));
        return;
      }
      const caller_req_id = `admin-${nanoid()}-${Date.now()}`;
      const timeout = setTimeout(() => {
        if (pendingRef.current.has(caller_req_id)) {
          pendingRef.current.delete(caller_req_id);
          reject(new Error("Request timed out"));
        }
      }, 15000);
      pendingRef.current.set(caller_req_id, {
        resolve: (v) => { clearTimeout(timeout); resolve(v); },
        reject: (e) => { clearTimeout(timeout); reject(e); },
      });
      socket.emit("apiReq", {
        type: "apiReq",
        steps: [{ name: "client req", started_at: Date.now() }],
        payload: {
          caller_req_id,
          req: { path, method, ...req },
        },
      });
    });
  }, []);

  const fetchTasks = useCallback(async (limit: number, skip: number, ticker?: string): Promise<SimTask[]> => {
    const query: Record<string, string> = { task_types: "Symbol Delisting", limit: String(limit), skip: String(skip) };
    if (ticker) query.filter_1 = ticker;
    const result = await emit("/admin/tasks", "GET", { query });
    return result?.data ?? [];
  }, [emit]);

  const submitDelistRequest = useCallback(async (ticker: string): Promise<void> => {
    await emit("/admin/position/delisted", "POST", {
      body: { symbol: ticker },
    });
  }, [emit]);

  return { connected, fetchTasks, submitDelistRequest };
};
